import { useMemo, useState } from 'react'

import { EmptyState, ErrorState, NoResultsState, SkeletonRows } from '@/shared/components/data'
import { Segmented } from '@/shared/components/ui'
import { useI18n } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { Stage, Task, TaskCreateInput } from '@/shared/types/domain'

import {
  useCompleteTask,
  useCreateTask,
  useDeleteTask,
  useProjectTasks,
  useUpdateTask,
} from '../api'
import { useMoveTask } from '../reorder'

import { TaskDetailSheet } from './TaskDetailSheet'
import { TaskQuickAdd } from './TaskQuickAdd'
import { TaskRow } from './TaskRow'

export type TaskFilter = 'abiertas' | 'todas' | 'hechas'

export interface TaskListProps {
  projectId: string
  stages: readonly Stage[]
  /** Sin alta ni edicion. */
  readOnly?: boolean
  /** Llega con el cursor en el alta rapida (lo usa el conmutador `⌘K`). */
  focusQuickAdd?: boolean
  className?: string
}

/** Lista de tareas de un proyecto, con sus tres estados y CRUD optimista. */
export function TaskList({ projectId, stages, readOnly, focusQuickAdd, className }: TaskListProps) {
  const { t, formatNumber } = useI18n()
  const [filter, setFilter] = useState<TaskFilter>('abiertas')
  const [openTask, setOpenTask] = useState<Task | null>(null)

  // El valor es la clave del filtro y no cambia con el idioma; lo unico que se
  // traduce es como se lee.
  const filters = useMemo(
    () => [
      { value: 'abiertas' as const, label: t('tasks.filter.open') },
      { value: 'todas' as const, label: t('tasks.filter.all') },
      { value: 'hechas' as const, label: t('tasks.filter.done') },
    ],
    [t],
  )

  const query = useProjectTasks(projectId)
  const create = useCreateTask(projectId)
  const update = useUpdateTask()
  const complete = useCompleteTask()
  const move = useMoveTask()
  const remove = useDeleteTask()

  // Misma query y mismo orden que el tablero: es el MISMO campo, así que las dos
  // vistas no pueden contradecirse aunque una se repinte antes que la otra. El
  // orden por rank ya lo aplicó `useProjectTasks` al llegar del API.
  const all = query.data ?? []
  const tasks = all.filter((task) =>
    filter === 'todas' ? true : filter === 'hechas' ? task.completed_at : !task.completed_at,
  )

  // El sheet lee de la cache y no de su propio estado: asi un cambio optimista
  // se ve en el panel y en la fila al mismo tiempo.
  const selected = openTask ? (all.find((task) => task.id === openTask.id) ?? null) : null

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <Segmented
          label={t('tasks.filter.label')}
          value={filter}
          options={filters}
          onChange={(next) => setFilter(next)}
        />
        <span className="num text-2xs text-text-3">
          {t('tasks.filter.shown', {
            visibles: formatNumber(tasks.length),
            total: formatNumber(all.length),
          })}
        </span>
      </div>

      {readOnly ? null : (
        <TaskQuickAdd
          projectId={projectId}
          pending={create.isPending}
          autoFocus={focusQuickAdd}
          // `mutateAsync` y no `mutate`: el formulario limpia el campo sólo si
          // el servidor aceptó, y para eso necesita la promesa.
          onCreate={(input: TaskCreateInput) => create.mutateAsync(input)}
        />
      )}

      {query.isPending ? (
        <SkeletonRows rows={6} columns={[0.1, 0.5, 0.18, 0.14]} withSpine={false} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : all.length === 0 ? (
        <EmptyState title={t('tasks.empty.title')} description={t('tasks.empty.listDescription')} />
      ) : tasks.length === 0 ? (
        <NoResultsState onClearFilters={() => setFilter('todas')} />
      ) : (
        <div className="hairline overflow-hidden rounded-[var(--radius-card)] bg-surface-1">
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              readOnly={readOnly}
              onOpen={setOpenTask}
              onToggleDone={(current, done) => complete.mutate({ id: current.id, done })}
              // El estado se cambia desde la fila, sin abrir el panel: es el
              // mismo `POST /tasks/{id}/move` que el selector del panel.
              stages={stages}
              onMoveToStage={(current, stage) =>
                move.mutate({ id: current.id, beforeId: null, afterId: null, stage })
              }
              // La fecha se corrige sin abrir la tarea: es lo único de la fila
              // que cambia a diario. Usa la misma mutación optimista que el
              // panel, así que la fila se actualiza en el acto.
              // El responsable, igual: misma mutación optimista que el panel.
              onChangeAssignee={(current, assigneeId, assignee) =>
                update.mutate({
                  id: current.id,
                  patch: { assignee_id: assigneeId },
                  optimistic: (actual) => ({ ...actual, assignee_id: assigneeId, assignee }),
                })
              }
              onChangeDue={(current, fecha) =>
                update.mutate({
                  id: current.id,
                  patch: { due_date: fecha },
                  optimistic: (actual) => ({ ...actual, due_date: fecha }),
                })
              }
            />
          ))}
        </div>
      )}

      <TaskDetailSheet
        task={selected}
        stages={stages}
        onClose={() => setOpenTask(null)}
        onPatch={(task, patch, optimistic) => update.mutate({ id: task.id, patch, optimistic })}
        onMoveToStage={(task, stage) =>
          move.mutate({ id: task.id, beforeId: null, afterId: null, stage })
        }
        onToggleDone={(current, done) => complete.mutate({ id: current.id, done })}
        onDelete={(task) => remove.mutate(task.id)}
      />
    </div>
  )
}
